import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { listJournalEntries, createManualJournal, reverseJournalEntry, listGLAccounts, flagJournalEntry } from '../api';
import { formatCurrency } from '../utils/currency';
import { useConfirm } from './ConfirmProvider';

function GeneralLedger({ authAxios, showFormMessage, userRole }) {
  const confirm = useConfirm();
  const [entries, setEntries] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [formData, setFormData] = useState({ date: new Date().toISOString().slice(0, 10), description: '', lines: [{ account_id: '', description: '', debit: '', credit: '' }, { account_id: '', description: '', debit: '', credit: '' }] });

  const canManage = useMemo(() => ['PARTNER', 'SUPERUSER', 'ACCOUNTANT'].includes(userRole), [userRole]);

  const fetchEntries = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await listJournalEntries(authAxios);
      setEntries(Array.isArray(data) ? data : []);
    } catch { showFormMessage('error', 'Failed to load journal entries.'); }
    finally { setIsLoading(false); }
  }, [authAxios, showFormMessage]);

  const fetchAccounts = useCallback(async () => {
    try {
      const data = await listGLAccounts(authAxios, { is_active: true, page_size: 500 });
      setAccounts(Array.isArray(data) ? data : []);
    } catch {}
  }, [authAxios]);

  useEffect(() => { fetchEntries(); fetchAccounts(); }, [fetchEntries, fetchAccounts]);

  const addLine = () => setFormData(f => ({ ...f, lines: [...f.lines, { account_id: '', description: '', debit: '', credit: '' }] }));
  const removeLine = (idx) => setFormData(f => ({ ...f, lines: f.lines.filter((_, i) => i !== idx) }));
  const updateLine = (idx, field, value) => setFormData(f => {
    const lines = [...f.lines];
    lines[idx] = { ...lines[idx], [field]: value };
    return { ...f, lines };
  });

  const totalDebit = useMemo(() => formData.lines.reduce((s, l) => s + (parseFloat(l.debit) || 0), 0), [formData.lines]);
  const totalCredit = useMemo(() => formData.lines.reduce((s, l) => s + (parseFloat(l.credit) || 0), 0), [formData.lines]);
  const isBalanced = Math.abs(totalDebit - totalCredit) < 0.01 && totalDebit > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isBalanced) { showFormMessage('error', 'Journal entry must be balanced (debits = credits).'); return; }
    try {
      const payload = {
        date: formData.date,
        description: formData.description,
        lines: formData.lines.filter(l => l.account_id).map(l => ({
          account_id: parseInt(l.account_id),
          description: l.description,
          debit: parseFloat(l.debit) || 0,
          credit: parseFloat(l.credit) || 0,
        })),
      };
      await createManualJournal(authAxios, payload);
      showFormMessage('success', 'Journal entry created.');
      setShowForm(false);
      setFormData({ date: new Date().toISOString().slice(0, 10), description: '', lines: [{ account_id: '', description: '', debit: '', credit: '' }, { account_id: '', description: '', debit: '', credit: '' }] });
      fetchEntries();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to create journal entry.');
    }
  };

  const handleReverse = async (id) => {
    const reason = await confirm({
      title: 'Reverse Journal Entry',
      message: 'Enter a reason for reversing this journal entry.',
      confirmText: 'Reverse',
      variant: 'danger',
      promptMode: true,
      promptLabel: 'Reason',
      required: true,
    });
    if (!reason) return;
    try {
      await reverseJournalEntry(authAxios, id, reason);
      showFormMessage('success', 'Reversal entry created.');
      fetchEntries();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to reverse.');
    }
  };

  const handleFlag = async (id, currentlyFlagged) => {
    if (currentlyFlagged) {
      try {
        await flagJournalEntry(authAxios, id, { is_flagged: false });
        showFormMessage('success', 'Entry unflagged.');
        fetchEntries();
      } catch (err) {
        showFormMessage('error', err.response?.data?.detail || 'Failed to unflag.');
      }
      return;
    }
    const reason = await confirm({
      title: 'Flag Journal Entry',
      message: 'Enter a reason for flagging this entry for review.',
      confirmText: 'Flag',
      variant: 'warning',
      promptMode: true,
      promptLabel: 'Reason',
    });
    if (!reason) return;
    try {
      await flagJournalEntry(authAxios, id, { is_flagged: true, flag_reason: reason });
      showFormMessage('success', 'Entry flagged for review.');
      fetchEntries();
    } catch (err) {
      showFormMessage('error', err.response?.data?.detail || 'Failed to flag.');
    }
  };

  const filteredEntries = useMemo(() => entries.filter(e => {
    if (showFlaggedOnly && !e.is_flagged) return false;
    if (sourceFilter && e.source_type !== sourceFilter) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      return (e.entry_number || '').toLowerCase().includes(term) || (e.description || '').toLowerCase().includes(term);
    }
    return true;
  }), [entries, sourceFilter, searchTerm, showFlaggedOnly]);

  const SOURCE_OPTIONS = ['MANUAL', 'AP_BILL', 'AP_PAYMENT', 'PETTY_CASH', 'EXPENSE', 'INVOICE'];

  return (
    <div className="animate__animated animate__fadeIn">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6 gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">General Ledger</h2>
          <p className="text-gray-500 text-sm mt-1">Journal entries and double-entry records</p>
        </div>
        {canManage && (
          <button onClick={() => setShowForm(true)} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium">
            + Manual Journal Entry
          </button>
        )}
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3 mb-4">
        <input type="text" placeholder="Search entry number or memo..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500" />
        <select value={sourceFilter} onChange={e => setSourceFilter(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg text-sm">
          <option value="">All Sources</option>
          {SOURCE_OPTIONS.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
        </select>
        <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700">
          <input type="checkbox" checked={showFlaggedOnly} onChange={e => setShowFlaggedOnly(e.target.checked)}
            className="rounded border-gray-300 text-orange-500 focus:ring-orange-400" />
          Flagged only
        </label>
      </div>

      {/* Manual Journal Entry Form */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold mb-4">New Manual Journal Entry</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input type="date" required value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Memo</label>
                  <input type="text" required value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" placeholder="Journal entry description" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Journal Lines</label>
                  <button type="button" onClick={addLine} className="text-blue-600 hover:text-blue-800 text-xs font-medium">+ Add Line</button>
                </div>
                <div className="space-y-2">
                  {formData.lines.map((line, idx) => (
                    <div key={idx} className="grid grid-cols-12 gap-2 items-center">
                      <select value={line.account_id} onChange={e => updateLine(idx, 'account_id', e.target.value)}
                        className="col-span-4 px-2 py-2 border border-gray-300 rounded-lg text-sm">
                        <option value="">Select Account</option>
                        {accounts.map(a => <option key={a.id} value={a.id}>{a.code} – {a.name}</option>)}
                      </select>
                      <input type="text" placeholder="Description" value={line.description} onChange={e => updateLine(idx, 'description', e.target.value)}
                        className="col-span-3 px-2 py-2 border border-gray-300 rounded-lg text-sm" />
                      <input type="number" step="0.01" min="0" placeholder="Debit" value={line.debit} onChange={e => updateLine(idx, 'debit', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <input type="number" step="0.01" min="0" placeholder="Credit" value={line.credit} onChange={e => updateLine(idx, 'credit', e.target.value)}
                        className="col-span-2 px-2 py-2 border border-gray-300 rounded-lg text-sm text-right" />
                      <button type="button" onClick={() => removeLine(idx)} className="col-span-1 text-red-400 hover:text-red-600 text-sm">✕</button>
                    </div>
                  ))}
                </div>
                <div className="flex justify-end gap-6 mt-2 text-sm font-medium">
                  <span>Debit: {formatCurrency(totalDebit)}</span>
                  <span>Credit: {formatCurrency(totalCredit)}</span>
                  <span className={isBalanced ? 'text-green-600' : 'text-red-600'}>{isBalanced ? '✓ Balanced' : '✗ Unbalanced'}</span>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <button type="button" onClick={() => setShowForm(false)} className="px-4 py-2 text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 text-sm">Cancel</button>
                <button type="submit" disabled={!isBalanced} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm disabled:opacity-50">
                  Post Entry
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Journal Entries List */}
      {isLoading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div></div>
      ) : filteredEntries.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl shadow-sm">
          <p className="text-gray-500">No journal entries found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Entry #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Memo</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredEntries.map(e => (
                <React.Fragment key={e.id}>
                  <tr className="hover:bg-gray-50 cursor-pointer" onClick={() => setExpandedEntry(expandedEntry === e.id ? null : e.id)}>
                    <td className="px-4 py-3 font-mono text-blue-600">{e.entry_number}</td>
                    <td className="px-4 py-3 text-gray-700">{e.date}</td>
                    <td className="px-4 py-3 text-gray-800">{e.description}</td>
                    <td className="px-4 py-3">
                      <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                        {(e.source_type || '').replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center">
                      {e.is_reversed && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-600 mr-1">Reversed</span>}
                      {e.is_flagged && <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-700" title={e.flag_reason || ''}>⚑ Flagged</span>}
                    </td>
                    <td className="px-4 py-3 text-right space-x-2">
                      {canManage && (
                        <button onClick={(ev) => { ev.stopPropagation(); handleFlag(e.id, e.is_flagged); }}
                          className={`text-xs font-medium ${e.is_flagged ? 'text-orange-600 hover:text-orange-800' : 'text-gray-400 hover:text-orange-500'}`}>
                          {e.is_flagged ? 'Unflag' : 'Flag'}
                        </button>
                      )}
                      {canManage && !e.is_reversed && (
                        <button onClick={(ev) => { ev.stopPropagation(); handleReverse(e.id); }} className="text-red-500 hover:text-red-700 text-xs font-medium">
                          Reverse
                        </button>
                      )}
                    </td>
                  </tr>
                  {expandedEntry === e.id && e.lines && (
                    <tr>
                      <td colSpan="6" className="px-8 py-3 bg-gray-50">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-gray-500">
                              <th className="text-left py-1">Account</th>
                              <th className="text-left py-1">Description</th>
                              <th className="text-right py-1">Debit</th>
                              <th className="text-right py-1">Credit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {e.lines.map((l, i) => (
                              <tr key={i} className="border-t border-gray-200">
                                <td className="py-1 font-mono">{l.account_code} – {l.account_name}</td>
                                <td className="py-1 text-gray-600">{l.description}</td>
                                <td className="py-1 text-right">{parseFloat(l.debit) > 0 ? formatCurrency(l.debit) : ''}</td>
                                <td className="py-1 text-right">{parseFloat(l.credit) > 0 ? formatCurrency(l.credit) : ''}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default GeneralLedger;
